import React from 'react';
import { motion } from 'framer-motion';
import { useConfig } from '@context/ConfigContext';
import Button from '@components/shared/Button/Button';
import styles from './CTABlock.module.css';

const CTABlock: React.FC = () => {
  const { config } = useConfig();

  if (!config || !config.components.ctaBlocks.enabled) return null;

  const { ctaBlocks } = config.components;

  if (!ctaBlocks.items || ctaBlocks.items.length === 0) {
    return null;
  }

  const handleButtonClick = (link: string, e: React.MouseEvent<HTMLButtonElement>) => {
    // Remove focus from button immediately after click
    e.currentTarget.blur();
    
    if (link.startsWith('#')) {
      const element = document.querySelector(link);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });
      }
    } else if (link.startsWith('tel:') || link.startsWith('mailto:')) {
      // Use anchor click for tel: and mailto: links to avoid mobile browser issues
      const anchor = document.createElement('a');
      anchor.href = link;
      anchor.click();
    } else {
      window.location.href = link;
    }
  };

  return (
    <>
      {ctaBlocks.items.map((block) => (
        <section
          key={block.id}
          id={block.id}
          className={styles.ctaBlock}
          style={{
            backgroundImage: block.backgroundImage
              ? `url('${block.backgroundImage}')`
              : undefined,
          }}
        >
          <div className={styles.overlay}></div>
          <div className={styles.container}>
            <motion.div
              className={styles.content}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.8, ease: 'easeOut' }}
            >
              <motion.h2
                className={styles.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {block.title}
              </motion.h2>

              <motion.p
                className={styles.text}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {block.text}
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.6 }}
              >
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={(e) => handleButtonClick(block.buttonLink, e)}
                  className={styles.ctaButton}
                >
                  {block.buttonText}
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </section>
      ))}
    </>
  );
};

export default CTABlock;